import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../shared/core/discriminated-union-read.schema";
import { enumReadSchema } from "../../shared/core/enum-read.schema";
import { vibeSafeReturnPath } from "../VibeTenant/vibe-sign-in-endpoints";

/**
 * What an operator declares so the platform can prove their app WORKS after a
 * deploy, rather than only that its shell renders.
 *
 * **The failure this exists for.** The post-deploy check drives the app's health
 * path and judges the STATUS CODE. A data route can return 500 on every request,
 * or a 200 carrying `{"error":"…"}`, while the shell responds, the build sha
 * matches and a content-marker grep passes. Every signal the platform publishes
 * is satisfied and the operator's screen is broken. The general rule, and this is
 * the second area of the platform to learn it: **a health check that asserts a
 * 200 cannot see a failure whose symptom is a different 200.** So a smoke route
 * asserts the property that actually differs — what the response BODY says.
 *
 * Three separable things are declared here, and they are independent:
 *
 * - `path` — WHICH route is exercised. The health path is the shell; these are
 *   the routes the operator says matter.
 * - `assert` — WHAT counts as working. Absent, this degrades to today's
 *   status-only check, which is a deliberate opt-in rather than a silent default.
 * - `auth` — WHETHER the platform presents the app's edge credential. Most routes
 *   worth checking sit behind the authorizer, and an unauthenticated probe of one
 *   observes a 401 forever.
 */

export const VIBE_APP_SMOKE_BOUNDS = {
  /// How many routes one app may declare. A cap rather than a limit anyone will
  /// reach: the smoke runs once per deploy, and a list long enough to matter is
  /// a test suite, which belongs in the app's own CI.
  routes: { max: 10 },
  /// Operator-facing label, quoted verbatim in the failure reason.
  name: { minLength: 1, maxLength: 64 },
  /// Same ceiling as the health path, for the same reason — the de-facto safe
  /// URL-path length across proxies.
  path: { maxLength: 2048 },
  /// Any real HTTP status. Not narrowed to 2xx: an operator proving a route is
  /// correctly gated may legitimately expect a 401 or a 404.
  status: { min: 100, max: 599 },
  /// Dotted path into the parsed body. Bounded so a pathological value cannot
  /// turn into a long walk over an attacker-influenced document.
  jsonPath: { minLength: 1, maxLength: 256 },
  /// Ceiling on a string compared with `json_path_equals`.
  equalsValue: { maxLength: 512 }
} as const;

/**
 * How much of a response body is read before the reader stops.
 *
 * The probe fetches an operator's own route, so the body is arbitrary — an app
 * that streams forever, or answers a smoke request with a 200 MiB export, must
 * not be able to hold a control-plane worker or exhaust its heap. Truncating is
 * safe for the assertions here: a body cut at this length no longer parses as
 * JSON, which is a FAILED verdict about the route rather than a silent pass.
 */
export const VIBE_APP_SMOKE_BODY_MAX_BYTES = 64 * 1024;

/**
 * Per-route HTTP timeout. Longer than the served probe's 5s because that one
 * makes a `HEAD` against an edge that is already known to answer, while this
 * drives a real data route that legitimately queries a datastore.
 */
export const VIBE_APP_SMOKE_TIMEOUT_MS = 10_000;

export const VIBE_SMOKE_ROUTE_AUTH_MODES = ["none", "edge_token"] as const;

/**
 * `edge_token` sends the app's own per-app edge-auth token — the credential the
 * platform already holds, already encrypts at rest and already presents on the
 * served probe. Deliberately NOT a new stored user credential: a smoke that
 * needs the operator to park a password in the platform is a secret to rotate, a
 * blast radius to reason about and a migration, in exchange for reaching routes
 * behind the app's OWN login rather than behind the platform's authorizer.
 * Reaching those is a separate decision, and this enum is where it would land.
 */
export const VibeSmokeRouteAuthSchema = z.enum(VIBE_SMOKE_ROUTE_AUTH_MODES);

/** `VibeSmokeRouteAuthSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeSmokeRouteAuthReadSchema = enumReadSchema(VIBE_SMOKE_ROUTE_AUTH_MODES);
export type VibeSmokeRouteAuth = z.infer<typeof VibeSmokeRouteAuthSchema>;

/**
 * A dotted path into a parsed JSON body: `data.items.0.id`.
 *
 * Numeric segments index arrays, so no bracket syntax is needed and there is one
 * spelling of any given path. The grammar is closed on purpose — this is a
 * reader, not a query language, and every construct it does not have is one that
 * cannot be got wrong in an app's config.
 */
export const VibeSmokeJsonPathSchema = z
  .string()
  .min(VIBE_APP_SMOKE_BOUNDS.jsonPath.minLength)
  .max(VIBE_APP_SMOKE_BOUNDS.jsonPath.maxLength)
  .regex(
    /^[A-Za-z0-9_$-]+(\.[A-Za-z0-9_$-]+)*$/,
    "must be dot-separated segments of letters, digits, _, $ or -"
  );

/**
 * What the body has to say.
 *
 * `json_path_absent` is the one the reported incident needed: a 200 carrying an
 * error envelope is caught by asserting that `error` is NOT there, which no
 * status-code check can express.
 */
export const VibeSmokeAssertionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("json_path_absent"),
    path: VibeSmokeJsonPathSchema
  }),
  z.object({
    kind: z.literal("json_path_present"),
    path: VibeSmokeJsonPathSchema
  }),
  z.object({
    kind: z.literal("json_path_equals"),
    path: VibeSmokeJsonPathSchema,
    /// Compared by strict equality against the parsed JSON value, so the
    /// declared type is part of the assertion: `"200"` does not match `200`.
    value: z.union([
      z.string().max(VIBE_APP_SMOKE_BOUNDS.equalsValue.maxLength),
      z.number(),
      z.boolean(),
      z.null()
    ])
  })
]);
export type VibeSmokeAssertion = z.infer<typeof VibeSmokeAssertionSchema>;

/**
 * An assertion as a READER parses it: a kind this build has a branch for reads as
 * itself, a kind a newer backend added reads as the server's own word with every
 * field it sent — so one new assertion kind does not fail the whole app read.
 */
export const VibeSmokeAssertionReadSchema = z.union([
  VibeSmokeAssertionSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(VibeSmokeAssertionSchema) })
]);
export type VibeSmokeAssertionRead = z.infer<typeof VibeSmokeAssertionReadSchema>;

export const VibeAppSmokeRouteSchema = z.object({
  /// Operator's own label. Carried into the verdict so a failure names the route
  /// the operator recognises rather than a path they have to map back by hand.
  name: z
    .string()
    .min(VIBE_APP_SMOKE_BOUNDS.name.minLength)
    .max(VIBE_APP_SMOKE_BOUNDS.name.maxLength),
  /// Absolute path on the app's own public URL. Never a full URL: the origin is
  /// platform-stamped at app creation from `VIBE_APP_DOMAIN`, and keeping the
  /// host out of operator hands is what keeps this free of SSRF.
  ///
  /// 🔴 `startsWith("/")` IS NOT ENOUGH, and believing it was is how this shipped
  /// wrong the first time. `//evil.example/x` and `/\evil.example` both begin
  /// with a slash and are both read as PROTOCOL-RELATIVE, so
  /// `new URL(path, origin)` resolves them to `https://evil.example/…` — an
  /// off-host fetch wearing a path's clothes, and with `auth: "edge_token"` it
  /// hands the app's own credential to whatever host the config named.
  ///
  /// `vibeSafeReturnPath` is the repo's existing statement of exactly this rule,
  /// written for the sign-in `return_to` — one leading slash, and nothing a
  /// client would read as an origin, backslashes refused because some clients
  /// normalise them to slashes AFTER a check like this. Reused rather than
  /// restated: its own docstring is about two programs implementing one rule and
  /// disagreeing, and a third spelling would be that bug again.
  path: z
    .string()
    .startsWith("/")
    .max(VIBE_APP_SMOKE_BOUNDS.path.maxLength)
    .refine((candidate) => vibeSafeReturnPath(candidate) !== null, {
      message: "must be a path on the app's own host — no protocol-relative or backslash form"
    }),
  expectStatus: z
    .number()
    .int()
    .min(VIBE_APP_SMOKE_BOUNDS.status.min)
    .max(VIBE_APP_SMOKE_BOUNDS.status.max)
    .default(200),
  auth: VibeSmokeRouteAuthSchema.default("none"),
  /// `null` is status-only — today's check, said out loud.
  assert: VibeSmokeAssertionSchema.nullable().default(null)
});
export type VibeAppSmokeRoute = z.infer<typeof VibeAppSmokeRouteSchema>;

export const VibeAppSmokeRoutesSchema = z
  .array(VibeAppSmokeRouteSchema)
  .max(VIBE_APP_SMOKE_BOUNDS.routes.max);

/**
 * A smoke route as a READER of the app parses it: `auth` and the assertion's kind
 * read leniently, so a newer backend's auth mode or assertion kind reaches an
 * older console as an unlisted value instead of failing the app read. The write
 * schema above stays strict.
 */
export const VibeAppSmokeRouteReadSchema = VibeAppSmokeRouteSchema.extend({
  auth: VibeSmokeRouteAuthReadSchema.default("none"),
  assert: VibeSmokeAssertionReadSchema.nullable().default(null)
});

/**
 * The routes as a reader parses them. No `.max`: the bound is the writer's
 * invariant, and a newer backend raising it must not fail an older reader.
 */
export const VibeAppSmokeRoutesReadSchema = z.array(VibeAppSmokeRouteReadSchema);
